import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Bug, Sparkles, MessageSquare, AlertTriangle, Loader2, RefreshCw, ImageIcon, Trash2, X,
} from 'lucide-react';
import {
  getApiBase,
  FEEDBACK_TYPE_LABELS,
  FEEDBACK_STATUS_LABELS,
  type FeedbackRecord,
  type FeedbackStatus,
  type FeedbackType,
} from '@membersyncpro/feedback';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const TYPE_ICON: Record<FeedbackType, typeof Bug> = {
  bug: Bug,
  feature_request: Sparkles,
  general: MessageSquare,
  complaint: AlertTriangle,
};

const STATUS_VARIANT: Record<FeedbackStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  new: 'default',
  reviewed: 'secondary',
  in_progress: 'default',
  resolved: 'secondary',
  closed: 'outline',
  wont_fix: 'destructive',
};

const FILTERS: Array<{ label: string; value: FeedbackStatus | 'all' }> = [
  { label: 'All',         value: 'all' },
  { label: 'New',         value: 'new' },
  { label: 'In progress', value: 'in_progress' },
  { label: 'Resolved',    value: 'resolved' },
  { label: 'Closed',      value: 'closed' },
];

function relativeTime(iso: string): string {
  const d = new Date(iso.replace(' ', 'T') + 'Z'); // SQLite "2026-05-24 20:35:19" → ISO
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

export default function FeedbackList() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<FeedbackRecord[]>([]);
  const [filter, setFilter] = useState<FeedbackStatus | 'all'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const apiBase = useMemo(() => getApiBase(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) {
        setError('Not signed in.');
        return;
      }
      const url = new URL(`${apiBase}/v1/feedback`);
      if (filter !== 'all') url.searchParams.set('status', filter);
      url.searchParams.set('limit', '100');
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        setError("You don't have admin access. Ask the owner to add your email to SUPER_ADMIN_EMAILS.");
        return;
      }
      if (res.status === 503) {
        setError('Admin auth not configured on this environment (likely e2e/staging — only prod has the Clerk secret).');
        return;
      }
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return;
      }
      const json = (await res.json()) as { items: FeedbackRecord[]; count: number };
      setItems(json.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [apiBase, filter, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const doDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${apiBase}/v1/feedback/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const e = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(e.error ?? `HTTP ${res.status}`);
      }
      setItems((prev) => prev.filter((i) => i.id !== id));
      setPendingDeleteId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  }, [apiBase, getToken]);

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Feedback</h1>
          <p className="text-sm text-muted-foreground">
            All submissions, newest first. Filter by status.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="flex gap-1 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={
              'rounded-full px-3 py-1 text-xs font-medium transition-colors ' +
              (filter === f.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent')
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <Card>
          <CardContent className="py-6 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      {!error && !loading && items.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <MessageSquare className="mx-auto mb-3 h-8 w-8 opacity-40" />
            No feedback {filter === 'all' ? 'yet' : `with status "${FEEDBACK_STATUS_LABELS[filter as FeedbackStatus]}"`}.
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {items.map((item) => {
          const Icon = TYPE_ICON[item.type];
          return (
            <Card key={item.id}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <Icon className="h-4 w-4 shrink-0" />
                    <Badge variant="outline" className="shrink-0">
                      {FEEDBACK_TYPE_LABELS[item.type]}
                    </Badge>
                    <Badge variant={STATUS_VARIANT[item.status]} className="shrink-0">
                      {FEEDBACK_STATUS_LABELS[item.status]}
                    </Badge>
                    <Badge variant="outline" className="shrink-0">{item.app}</Badge>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground" title={item.created_at}>
                      {relativeTime(item.created_at)}
                    </span>
                    {pendingDeleteId === item.id ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">Delete?</span>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-7 px-2"
                          disabled={deletingId === item.id}
                          onClick={() => void doDelete(item.id)}
                        >
                          {deletingId === item.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            'Yes'
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          disabled={deletingId === item.id}
                          onClick={() => setPendingDeleteId(null)}
                          aria-label="Cancel delete"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-muted-foreground hover:text-destructive"
                        onClick={() => setPendingDeleteId(item.id)}
                        aria-label="Delete feedback"
                        title="Delete this feedback"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>

                {item.subject && (
                  <h3 className="text-sm font-semibold">{item.subject}</h3>
                )}
                <p className="text-sm whitespace-pre-wrap">{item.message}</p>

                {item.attachments && item.attachments.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {item.attachments.map((a) => (
                      <a
                        key={a.id}
                        href={`${apiBase}${a.url}`}
                        target="_blank"
                        rel="noreferrer"
                        className="block relative group"
                        title={`${a.filename} — ${Math.ceil(a.size / 1024)}KB${a.kind === 'screenshot' ? ' (screenshot)' : ''}`}
                      >
                        <img
                          src={`${apiBase}${a.url}`}
                          alt={a.filename}
                          className="h-20 w-20 object-cover rounded border bg-muted group-hover:ring-2 group-hover:ring-primary transition"
                        />
                        {a.kind === 'screenshot' && (
                          <ImageIcon className="absolute bottom-1 right-1 h-3 w-3 text-white drop-shadow" />
                        )}
                      </a>
                    ))}
                  </div>
                )}

                {(item.user_email || item.context || Object.keys(item.metadata).length > 0 || item.internal_notes) && (
                  <div className="text-xs text-muted-foreground space-y-1 pt-1 border-t">
                    {item.user_email ? (
                      <div><span className="font-medium">From:</span> {item.user_name ? `${item.user_name} <${item.user_email}>` : item.user_email}</div>
                    ) : (
                      <div className="italic">From: anonymous (not signed in)</div>
                    )}
                    {item.context && <div><span className="font-medium">Path:</span> <code>{item.context}</code></div>}
                    {typeof item.metadata.href === 'string' && (
                      <div className="truncate"><span className="font-medium">URL:</span> <a href={String(item.metadata.href)} target="_blank" rel="noreferrer" className="underline">{String(item.metadata.href)}</a></div>
                    )}
                    {typeof item.metadata.title === 'string' && (
                      <div className="truncate"><span className="font-medium">Page title:</span> {String(item.metadata.title)}</div>
                    )}
                    {typeof item.metadata.viewport === 'string' && (
                      <div><span className="font-medium">Viewport:</span> {String(item.metadata.viewport)}
                        {typeof item.metadata.devicePixelRatio === 'number' && ` @ ${String(item.metadata.devicePixelRatio)}x`}
                      </div>
                    )}
                    {(typeof item.metadata.language === 'string' || typeof item.metadata.timezone === 'string') && (
                      <div>
                        {typeof item.metadata.language === 'string' && <><span className="font-medium">Lang:</span> {String(item.metadata.language)}</>}
                        {typeof item.metadata.language === 'string' && typeof item.metadata.timezone === 'string' && ' · '}
                        {typeof item.metadata.timezone === 'string' && <><span className="font-medium">TZ:</span> {String(item.metadata.timezone)}</>}
                      </div>
                    )}
                    {typeof item.metadata.userAgent === 'string' && (
                      <div className="truncate"><span className="font-medium">UA:</span> {String(item.metadata.userAgent)}</div>
                    )}
                    {typeof item.metadata.referrer === 'string' && (
                      <div className="truncate"><span className="font-medium">Referrer:</span> {String(item.metadata.referrer)}</div>
                    )}
                    {item.internal_notes && (
                      <div className="mt-1"><span className="font-medium">Notes:</span> {item.internal_notes}</div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
